import { User } from "firebase/auth";
import {
  upsertUser,
  setMyReferralCodeIfNull,
  getUserCredits,
} from "@dataconnect/generated";
import { functions } from "@/src/config/firebase";
import { httpsCallable } from "firebase/functions";
import { reportError } from "../utils/errorReporting";
import { AppError } from "../utils/appError";
import {
  clearPendingReferralCode,
  generateReferralCode,
  readPendingReferralCode,
} from "./referralService";
import { trackReferralSignupAttributed } from "./analyticsService";
import {
  getCurrentNormalizedLanguage,
  notifyLanguageSyncProfileUpserted,
} from "./languageSyncService";
import type { SupportedLanguage } from "../utils/languageSync";

/**
 * Returns true if the error is a transient failure that should be retried.
 * Retries on network failures ("Failed to fetch") and 503/UNAVAILABLE errors.
 * Does not retry on auth/permission errors.
 *
 * Firebase Data Connect throws FirebaseError with string `code` fields
 * (e.g. "dataconnect/other", "dataconnect/unauthorized") rather than
 * numeric HTTP status codes. Transient backend failures (503) surface as
 * code "dataconnect/other" with UNAVAILABLE/503 in the message.
 */
function isRetryableError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  // Network-level failure (fetch didn't complete)
  if (error.message.includes("Failed to fetch")) {
    return true;
  }

  // Firebase errors use a string `code` property
  const code = (error as { code?: string }).code;

  // Do not retry on auth/permission errors
  if (
    code === "dataconnect/unauthorized" ||
    code === "dataconnect/permission-denied" ||
    code === "functions/permission-denied" ||
    code === "functions/unauthenticated"
  ) {
    return false;
  }

  // Retry on transient backend errors (503 / UNAVAILABLE)
  if (code === "dataconnect/other" || code === "dataconnect/unavailable") {
    return true;
  }

  // Also check the message for 503/UNAVAILABLE in case the code doesn't match
  if (error.message.includes("503") || error.message.includes("UNAVAILABLE")) {
    return true;
  }

  return false;
}

/**
 * Retries an async operation with exponential backoff on transient failures.
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  context: string,
  maxAttempts: number = 3
): Promise<T> {
  const delays = [1000, 2000]; // backoff delays between attempts
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const isLastAttempt = attempt === maxAttempts;
      if (isLastAttempt || !isRetryableError(error)) {
        throw error;
      }
      console.warn(
        `[${context}] Attempt ${attempt}/${maxAttempts} failed, retrying in ${delays[attempt - 1]}ms...`,
        error
      );
      await new Promise((resolve) => setTimeout(resolve, delays[attempt - 1]));
    }
  }
  // Unreachable, but satisfies TypeScript
  throw new AppError(
    "user/retry_exhausted",
    `withRetry: exhausted ${maxAttempts} attempts`
  );
}

/**
 * Ensures user exists in the database with trial setup.
 * This should only be called once when a user first authenticates.
 * The database will automatically set:
 * - status: TRIAL
 * - credits: 100
 * on first insert. Subsequent calls will only update the username if changed.
 */
const initializePromises = new Map<string, Promise<void>>();

export async function initializeUser(user: User): Promise<void> {
  // Deduplicate concurrent calls for the same user (e.g. rapid auth state changes).
  const existing = initializePromises.get(user.uid);
  if (existing) {
    return existing;
  }

  const promise = doInitializeUser(user).finally(() => {
    initializePromises.delete(user.uid);
  });
  initializePromises.set(user.uid, promise);

  return promise;
}

// True if the auth user was just created (first-ever sign-in). Firebase reports
// `creationTime` and `lastSignInTime` as HTTP date strings with second-level
// precision; on a brand-new signup they're written as part of the same
// operation and tend to match within a few seconds. A 5-second window absorbs
// any minor clock-skew without misclassifying returning users.
const FRESH_SIGNUP_WINDOW_MS = 5000;

function isFreshSignup(user: User): boolean {
  const created = user.metadata.creationTime;
  const lastSignIn = user.metadata.lastSignInTime;
  if (!created || !lastSignIn) return false;
  const createdMs = Date.parse(created);
  const lastSignInMs = Date.parse(lastSignIn);
  if (Number.isNaN(createdMs) || Number.isNaN(lastSignInMs)) return false;
  return Math.abs(createdMs - lastSignInMs) <= FRESH_SIGNUP_WINDOW_MS;
}

/**
 * Best-effort referral attribution. Does NOT throw: any failure here must not
 * block the rest of `initializeUser`. Stripe customer creation, HubSpot sync,
 * and routing all depend on initializeUser completing.
 *
 * Steps:
 *   1. Ensure the user has a referralCode (idempotent — set only if NULL).
 *   2. If a pending `?ref=` is in localStorage and this is a fresh signup,
 *      call recordReferralSignup so the backend can validate + email the team.
 */
async function applyReferralAttribution(user: User): Promise<void> {
  try {
    await setMyReferralCodeIfNull({ referralCode: generateReferralCode() });
  } catch (error) {
    // Collisions on a 30^8 alphabet are vanishingly rare and the next login
    // retries naturally. Log + continue.
    reportError(error, {
      context: "initializeUser:setMyReferralCodeIfNull",
      userEmail: user.email,
    });
  }

  const pendingCode = readPendingReferralCode();
  if (!pendingCode) return;

  // From here on, always clear the pending code in `finally` — whether the
  // user is fresh, the call succeeds, or it fails — so we never replay the
  // same code on a future signup from the same browser.
  try {
    if (!isFreshSignup(user)) return;

    const recordReferralSignup = httpsCallable<
      { code: string },
      { recorded: boolean }
    >(functions, "recordReferralSignup");
    const result = await recordReferralSignup({ code: pendingCode });
    if (result.data.recorded) {
      trackReferralSignupAttributed(pendingCode);
    }
  } catch (error) {
    // Non-fatal — silently dropping referral attribution is preferable to
    // failing the user's signup. The team gets no email; nothing breaks.
    reportError(error, {
      context: "initializeUser:recordReferralSignup",
      userEmail: user.email,
      referralCode: pendingCode,
    });
  } finally {
    clearPendingReferralCode();
  }
}

async function doInitializeUser(user: User): Promise<void> {
  // stripeCustomerId is intentionally omitted from the upsert — it is written
  // atomically by the createStripeCustomer Cloud Function (guarded by an
  // isNull-where clause + @unique column) and is immutable once set.
  try {
    // Seed the UI language with the row itself (SIS-483): the languageSync
    // listener fires before this upsert on first signup, its update touches
    // 0 rows, and a user who never switches language would otherwise stay
    // NULL. undefined (unknown language) leaves the column untouched.
    await withRetry(
      () =>
        upsertUser({
          username: user.email || user.uid,
          language: getCurrentNormalizedLanguage() ?? undefined,
        }),
      "initializeUser:upsertUser"
    );
    // This upsert snapshots the language on every login and can land after
    // a newer languageSync write — tell the sync to re-assert the latest
    // selection (PR #993 review).
    notifyLanguageSyncProfileUpserted();
  } catch (error) {
    reportError(error, {
      context: "initializeUser:upsertUser",
      userEmail: user.email,
    });
    console.error("Failed to upsert user:", error);
    throw error;
  }

  // Runs after upsertUser so the User row exists for the CreateReferral FK.
  // Fire-and-forget — must never block initialization. The function handles
  // its own errors internally and never throws.
  void applyReferralAttribution(user);

  // Skip the createStripeCustomer CF when the user already has an ID.
  // The CF is itself idempotent (early-outs in the DB-read path), but
  // invoking it on every login still costs a callable invocation, cold
  // start, App Check verification, and an extra Data Connect read inside
  // the CF. Reading stripeCustomerId here first lets us skip all of that
  // for returning users, who are the common case.
  let existingStripeCustomerId: string | null | undefined;
  try {
    const result = await getUserCredits();
    existingStripeCustomerId = result.data.user?.stripeCustomerId;
  } catch (error) {
    // On query failure, fall through to the CF — its own early-out and
    // error handling still apply.
    reportError(error, {
      context: "initializeUser:getUserCredits",
      userEmail: user.email,
    });
    existingStripeCustomerId = null;
  }

  if (!existingStripeCustomerId) {
    // Awaited (not fire-and-forget) so stripeCustomerId is in the DB
    // before the billing UI renders. The CF is idempotent: DB-first,
    // then Stripe metadata recovery, then create — all tied to
    // server-verified auth.uid.
    try {
      const createStripeCustomer = httpsCallable<
        { name: string | null },
        { customerId: string }
      >(functions, "createStripeCustomer", { timeout: 150_000 });
      await createStripeCustomer({ name: user.displayName || user.email });
    } catch (error) {
      // Non-fatal: a failed Stripe customer creation is retried on next login.
      reportError(error, {
        context: "initializeUser:createStripeCustomer",
        userEmail: user.email,
      });
      console.error("Failed to create Stripe customer:", error);
    }
  }

  // Promote the contact to SIGNED_UP in HubSpot, but only once the email is
  // verified. Gating on emailVerified keeps SIGNED_UP meaning "verified signup"
  // and — on the password sign-up path — avoids racing the unauthenticated
  // recordHubspotLead LEAD write that Login.tsx fires concurrently with account
  // creation: createUserWithEmailAndPassword authenticates immediately, so an
  // ungated upsert here could land SIGNED_UP and then be clobbered by the
  // slower LEAD upsert. Magic-link and OAuth users are already verified at first
  // auth, so they still promote immediately. Unverified password signups stay
  // at LEAD until they verify and return — this re-runs on the next auth-state
  // change (initializeUser only dedupes concurrent calls).
  if (user.emailVerified) {
    const upsertHubspotContact = httpsCallable<
      { language?: SupportedLanguage },
      { success: boolean; error?: string }
    >(functions, "upsertHubspotContact");
    // The sign-in language rides on this upsert so HubSpot's "Preferred
    // language" costs no extra request (SIS-578); a later in-session switch
    // is mirrored by languageSyncService. Unknown language: leave it alone.
    const language = getCurrentNormalizedLanguage();
    upsertHubspotContact(language ? { language } : {}).catch((error) => {
      reportError(error, {
        context: "upsertHubspotContact",
        userEmail: user.email,
      });
      console.error("Failed to sync with HubSpot:", error);
    });
  }
}
